import { assertEvent } from 'xstate5';

import {
  addModificationToActiveModificationsList,
  generateDerivedModifiers,
  getModifiersCount,
  removeModificationToActiveModificationsList,
} from '../../helpers';
import {
  type Guard,
  type Modification,
  type ModificationsMap,
  type Product,
} from '../../types';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Determines if currently selected modifications exceed the maximum allowed
 * modifications for a product.
 */
export const checkIfExceedsMaxModifications: Guard = function (args): boolean {
  const { context } = args;

  const { product, allModificationsMap, modifications } = context;

  const hasMissingData = !modifications || !allModificationsMap || !product;

  if (hasMissingData) {
    return false;
  }

  const { initial, active } = modifications;
  const { maxModifications } = product;

  if (checkIfHasUnlimitedModifications(product)) {
    return false;
  }

  return checkIfExceedMaxModifications({
    maxModifications,
    initialModifications: initial,
    activeModifications: active,
    allModificationsMap,
  });
};

/**
 * Determines if adding a modification exceeds the maximum allowed modifications for a product.
 */
export const checkIfExceedsMaxModificationsAfterAddition: Guard = function (
  args,
): boolean {
  const { context, event } = args;

  assertEvent(event, [
    'customization.add-modification',
    'customization.dressing.increase-portions',
  ]);

  const { ingredientId } = event;
  const { product, allModificationsMap, modifications } = context;

  const hasMissingData = !modifications || !allModificationsMap || !product;

  if (hasMissingData) {
    return false;
  }

  const { initial, active } = modifications;
  const { maxModifications } = product;

  if (checkIfHasUnlimitedModifications(product)) {
    return false;
  }

  const updatedActives = addModificationToActiveModificationsList(
    ingredientId,
    active,
    allModificationsMap,
  );

  return checkIfExceedMaxModifications({
    maxModifications,
    initialModifications: initial,
    activeModifications: updatedActives,
    allModificationsMap,
  });
};

/**
 * Determines if removing a modification exceeds the maximum allowed modifications for a product.
 */
export const checkIfExceedsMaxModificationsAfterRemoval: Guard = function (
  args,
): boolean {
  const { context, event } = args;

  assertEvent(event, [
    'customization.remove-modification',
    'customization.dressing.decrease-portions',
  ]);

  const { ingredientId } = event;
  const { product, allModificationsMap, modifications } = context;

  const hasMissingData = !modifications || !allModificationsMap || !product;

  if (hasMissingData) {
    return false;
  }

  const { initial, active } = modifications;
  const { maxModifications } = product;

  if (checkIfHasUnlimitedModifications(product)) {
    return false;
  }

  const updatedActives = removeModificationToActiveModificationsList({
    ingredientId,
    activeModifications: active,
    allModificationsMap,
  });

  return checkIfExceedMaxModifications({
    maxModifications,
    initialModifications: initial,
    activeModifications: updatedActives,
    allModificationsMap,
  });
};

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Determines if updated modifications exceed the maximum allowed modifications
 * for a product.
 */
function checkIfExceedMaxModifications(
  params: CheckIfExceedsMaxModificationsParams,
): boolean {
  const {
    maxModifications,
    initialModifications,
    activeModifications,
    allModificationsMap,
  } = params;

  const derivedModifiers = generateDerivedModifiers(initialModifications, activeModifications); // prettier-ignore
  const modifiersCount = getModifiersCount(derivedModifiers, allModificationsMap); // prettier-ignore

  return modifiersCount > maxModifications;
}

function checkIfHasUnlimitedModifications(product: Product) {
  return product.maxModifications === -1;
}

// ─── Types ───────────────────────────────────────────────────────────────────

type CheckIfExceedsMaxModificationsParams = {
  maxModifications: number;
  initialModifications: Modification[];
  activeModifications: Modification[];
  allModificationsMap: ModificationsMap;
};
